import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Subscription } from "rxjs";
import swal from "sweetalert2";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscArea,
  BscCiclo,
  BscCicloTrimestre,
  BscEjercicio,
  BscEjercicioKpi,
  BscHistorial,
  BscRes,
  EstatusPlaneacionBsc,
  ViaCapturaBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { BscService } from "../bsc.service";
import {
  EXIGIR_SUMA_POR_OMISION,
  RevisionPesos,
  TOLERANCIA_POR_OMISION,
  metaDeTexto,
  metaDistinta,
  pesosDistintos,
  revisarPesos,
  unidadDeMeta,
} from "../bsc-calculos";
import { ACCION_HISTORIAL, ESTATUS_PLANEACION, EstadoBsc } from "../bsc-estados";
import { SIN_DATO, fechaCorta, fechaHora, meta, pct } from "../bsc-formato";
import { DialogoPinBscComponent, abrirDialogoPinBsc } from "../dialogo-pin/dialogo-pin-bsc.component";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";

/**
 * Qué enseña la pantalla para el área y el año elegidos:
 *   CARGANDO        catálogos o la consulta en vuelo;
 *   SIN_CATALOGOS   no se pudieron leer las áreas (no se afirma que no haya);
 *   SIN_AREAS       el perfil no tiene ninguna área que planear;
 *   ERROR           la consulta falló (sin permiso, sin sesión, sin conexión);
 *   SIN_CICLO       el año no está dado de alta (Calidad lo abre en Parámetros);
 *   SIN_PLANEACION  el área no tiene planeación en el año: crearla o cargarla;
 *   PLANEACION      la tabla de metas y pesos.
 */
type EstadoPlaneacion =
  | "CARGANDO"
  | "SIN_CATALOGOS"
  | "SIN_AREAS"
  | "ERROR"
  | "SIN_CICLO"
  | "SIN_PLANEACION"
  | "PLANEACION";

/** Un error o aviso del servidor listo para mostrarse. El código va en el title, no en el texto. */
interface MensajeBsc {
  codigo: string;
  texto: string;
}

/** Una opción del combo de unidades (BscUnidad: %, No, dias). */
interface OpcionUnidad {
  valor: string;
  texto: string;
}

/** Un área del combo: las mías, o todas si reviso o administro el BSC. */
interface OpcionArea {
  idBscArea: number;
  nombre: string;
  via: ViaCapturaBsc;
}

/** Un KPI del año de origen que no está en la planeación y se puede volver a agregar. */
interface CandidatoOrigen {
  idKpi: number;
  objetivo: string;
  indicador: string;
  meta: string;
}

/**
 * Un renglón de la tabla. Se arma UNA vez por respuesta del servidor y los
 * campos se editan en el propio objeto: arreglos u objetos nuevos en cada
 * detección de cambios rehacen las filas y les quitan el foco (como en T1 y
 * en las opciones de T4).
 */
interface FilaPlan {
  /** trackBy: "k<idEjercicioKpi>" o "n<consecutivo>" para las nuevas. */
  clave: string;
  idEjercicioKpi: number | null;
  /** Del maestro; en una nueva, el KPI del año de origen elegido. */
  idKpi: number | null;
  nueva: boolean;
  objetivo: string;
  indicador: string;
  /** false = quitar (RETIRADO en el año). */
  conservar: boolean;
  /** La meta como se escribe ("95%", "6 productos"); si no se toca, viajan el valor y el texto guardados. */
  metaEscrita: string;
  metaInicial: string;
  metaValorGuardado: number | null;
  metaTextoGuardado: string | null;
  unidad: string;
  /** En porcentaje (25 = 0.25); nulo = vacío (0). */
  pesos: (number | null)[];
  /** true = NA en ese trimestre. */
  na: boolean[];
  /** El peso que tenía antes de marcar NA, para devolverlo si se desmarca. */
  pesoAntesDeNa: (number | null)[];
  motivo: string;

  /* ---------- El año de origen (nulo si el KPI no estaba) ---------- */
  origen: BscEjercicioKpi | null;
  metaAnterior: string;
  alcanceAnterior: string;
  pesosAnteriores: string;

  /* ---------- Calculado en vivo (recalcula), solo para pintar ---------- */
  suma: string;
  sumaClase: "" | "is-ok" | "is-aviso" | "is-error";
  sumaNota: string;
  /** La meta difiere del origen: el envío pedirá el motivo. */
  metaCambiada: boolean;
  pesosCambiados: boolean;
  motivoPendiente: boolean;
  /**
   * Se enseña el campo del motivo: la meta o los pesos cambiaron o ya trae
   * uno. No se vuelve a esconder hasta la siguiente respuesta (borrar el
   * texto no le quita el foco al campo).
   */
  conMotivo: boolean;
  pesosConError: boolean;
  /** Algo cambió desde la última respuesta: se manda al guardar y sus avisos del servidor ya no valen. */
  editada: boolean;
  huellaInicial: string;
  /** Los avisos del servidor sobre el KPI tal como está guardado. */
  avisos: MensajeBsc[];
  /** Nombres accesibles: empiezan con lo visible y dicen de qué KPI son. */
  nombre: string;
}

/** Un renglón de la bitácora de la planeación. */
interface Movimiento {
  clave: string;
  /** bsc_historial.accion (ENVIO_PLANEACION, CAMBIO_META…). */
  tipo: string;
  accion: string;
  quien: string;
  cuando: string;
  detalle: string;
}

const TRIMESTRES = [1, 2, 3, 4];

/** BscUnidad.TODAS si el servidor no las manda. */
const UNIDADES_POR_OMISION = ["%", "No", "dias"];

/** `CODIGO[ Qn]: mensaje` (BscAviso.texto() y la revisión de BscPlaneacionBuss). */
const FORMATO_MENSAJE = /^([A-Z][A-Z0-9_-]+)(?: (Q[1-4]))?: ([\s\S]+)$/;

/** Topes de BscPlaneacionBuss: objetivo e indicador 500, meta 120, motivo 1000. */
const LARGO_OBJETIVO = 500;
const LARGO_META = 120;
const LARGO_MOTIVO = 1000;

/** Rutas de las otras pantallas del módulo. */
const RUTA = {
  tablero: "/admin/bsc/tablero",
  cargaExcel: "/admin/bsc/carga-excel",
  parametros: "/admin/bsc/parametros",
};

/**
 * BSC · T3 Planeación de metas del año (docs/BSC.md §4, §7, §10 y Anexo B,
 * maqueta «T3-PlaneacionMetas»), con la regla de Irving: una vez
 * autorizadas, metas, pesos y NA no cambian en el año.
 *
 * Por área y año (las mías, o todas si reviso o administro el BSC): sin
 * planeación se crea a partir del año anterior autorizado, vacía, o se carga
 * del FOR-011 (T1 en modo PLANEACION). En BORRADOR o REGRESADA se edita cada
 * KPI —meta y unidad, pesos Q1..Q4 con su suma en vivo y NA por trimestre,
 * conservar o quitar, agregar— con la meta y el alcance del año de origen a
 * la vista y el motivo cuando la meta cambia; se guarda como borrador y se
 * envía a autorización. Dirección (llave admin/bsc/autorizar) la autoriza o
 * la regresa con su PIN (el regreso con comentario). ENVIADA y AUTORIZADA son
 * de solo lectura, con el candado a la vista.
 *
 * La suma de pesos y "la meta cambió" se previsualizan con bsc-calculos.ts
 * (espejo del motor) solo para pintar: el servidor manda. Lo que impide
 * enviar llega en `faltantes` (sobre lo guardado) y el mensaje del envío
 * rechazado es el del servidor.
 *
 * Base visual: la de T4 y T1 del módulo (hero y tarjetas table-page, bloques
 * bsc-bloque de bsc.shared.css, tabla table-page con mat-form-field outline
 * table-page__field) y el selector de año de A1 con el área en su barra.
 * Año y área viajan en la URL (?anio=&idBscArea=): así llega el enlace del
 * resultado de T1 y el de A1/A2.
 */
@Component({
  selector: "app-planeacion-metas-bsc",
  standalone: true,
  templateUrl: "./planeacion-metas-bsc.component.html",
  // dashboard-tarjetas.css para el área y el estatus que se proyectan en la
  // barra del selector: llevan el _ngcontent de esta pantalla (como T4).
  styleUrls: [
    "../../dashboard/shared/dashboard-tarjetas.css",
    "../bsc.shared.css",
    "./planeacion-metas-bsc.component.css",
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    SelectorCorteBscComponent,
  ],
})
export class PlaneacionMetasBscComponent implements OnInit, OnDestroy {
  @ViewChild(SelectorCorteBscComponent) selector?: SelectorCorteBscComponent;
  @ViewChild("comboArea") comboArea?: ElementRef<HTMLSelectElement>;

  readonly ruta = RUTA;
  readonly trimestres = TRIMESTRES;
  readonly largoObjetivo = LARGO_OBJETIVO;
  readonly largoMeta = LARGO_META;
  readonly largoMotivo = LARGO_MOTIVO;

  anioInicial: number | null = null;
  anio: number | null = null;
  idBscArea: number | null = null;
  anioEnCurso = new Date().getFullYear();

  estado: EstadoPlaneacion = "CARGANDO";
  loading = false;
  guardando = false;
  enviando = false;
  creando = false;
  error = "";

  /** Las áreas del combo, armadas una vez por catálogos. */
  opcionesArea: OpcionArea[] = [];
  /** Las unidades del combo: el mismo arreglo para todas las filas. */
  opcionesUnidad: OpcionUnidad[] = UNIDADES_POR_OMISION.map(opcionUnidad);
  /** El área del enlace no es de las que puedo abrir: se dice cuál se abrió. */
  avisoArea = "";

  ciclo: BscCiclo | null = null;
  ejercicio: BscEjercicio | null = null;
  ejercicioOrigen: BscEjercicio | null = null;
  kpisOrigen: BscEjercicioKpi[] = [];
  candidatosOrigen: CandidatoOrigen[] = [];
  filas: FilaPlan[] = [];
  faltantes: MensajeBsc[] = [];
  avisos: MensajeBsc[] = [];
  movimientos: Movimiento[] = [];
  fechasCiclo = "";
  objetivoSolser = "";
  /** El último regreso con su comentario, el último envío y la autorización (de la bitácora). */
  regreso: Movimiento | null = null;
  envio: Movimiento | null = null;
  autorizacion: Movimiento | null = null;

  puedeEditar = false;
  puedePlanear = false;
  puedeAutorizar = false;
  tienePin = false;
  puedeCargarExcel = false;
  puedeConfigurar = false;

  /** Hay cambios sin guardar (se recalcula en cada edición, no en cada detección de cambios). */
  sucio = false;
  filasConPesosMal = 0;
  motivosPendientes = 0;
  /** ?modo=PLANEACION&anio= para T1. */
  queryCargaExcel: Record<string, string | number> = {};
  queryTablero: Record<string, number> = {};

  /** Revisa (C2) o administra (T4): planea cualquier área de su empresa (BscAccesos.supervisa). */
  private supervisa = false;
  private catalogosCargados = false;
  private idAreaPedida: number | null = null;
  private consulta?: Subscription;
  private peticion?: Subscription;
  private dialogo?: MatDialogRef<DialogoPinBscComponent, BscRes>;
  private query$?: Subscription;
  private huella = "";
  private consecutivo = 0;
  private origenPorKpi = new Map<number, BscEjercicioKpi>();
  private preguntaAbierta = false;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.anioInicial = anioValido(params.get("anio"));
    this.idAreaPedida = idValido(params.get("idBscArea"));
    const menus = this.authService.getMenuList();
    this.supervisa =
      tieneAccesoMenu(menus, "admin/bsc/revision") || tieneAccesoMenu(menus, "admin/bsc/parametros");
    // Angular reutiliza la pantalla si solo cambian los parámetros (el
    // historial del navegador o un enlace a la misma ruta): se abre lo que
    // pide. Las navegaciones propias (replaceUrl) traen lo mismo y no hacen nada.
    this.query$ = this.route.queryParamMap.subscribe((q) => {
      const anio = anioValido(q.get("anio"));
      const area = idValido(q.get("idBscArea"));
      const otroAnio = anio != null && this.anio != null && anio !== this.anio;
      const otraArea = area != null && this.idBscArea != null && area !== this.idBscArea;
      if ((otroAnio || otraArea) && this.catalogosCargados) {
        if (otroAnio && anio != null) {
          this.anio = anio;
          this.selector?.fijarAnio(anio);
        }
        if (otraArea && area != null && this.opcionesArea.some((a) => a.idBscArea === area)) {
          this.idBscArea = area;
        }
        this.consultar();
      }
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.peticion?.unsubscribe();
    this.query$?.unsubscribe();
    // El cuadro del PIN vive en el overlay y sobrevive al cambio de ruta.
    this.dialogo?.close();
    if (this.preguntaAbierta && swal.isVisible()) {
      swal.close();
    }
  }

  /* ============================ Año y área ============================ */

  /** catalogos.do (lo pide el selector): el año en curso y las áreas que puedo abrir. */
  onCatalogos(res: BscRes): void {
    const catalogos = res.catalogos ?? {};
    this.anioEnCurso = catalogos.anioEnCurso ?? this.anioEnCurso;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.opcionesArea = [...(catalogos.areas ?? [])]
      .filter((a) => a.idBscArea != null && (this.supervisa || a.esMia === true))
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || (a.nombre ?? "").localeCompare(b.nombre ?? ""))
      .map((a: BscArea) => ({
        idBscArea: a.idBscArea as number,
        nombre: a.nombre || `Área ${a.idBscArea}`,
        via: a.viaCaptura ?? "AMBAS",
      }));
    const primeraVez = !this.catalogosCargados;
    this.catalogosCargados = true;
    if (this.idBscArea == null || !this.opcionesArea.some((a) => a.idBscArea === this.idBscArea)) {
      const pedida = this.opcionesArea.find((a) => a.idBscArea === this.idAreaPedida);
      this.idBscArea = pedida?.idBscArea ?? this.opcionesArea[0]?.idBscArea ?? null;
      this.avisoArea =
        primeraVez && this.idAreaPedida != null && !pedida && this.idBscArea != null
          ? `El área del enlace no está entre las que puedes planear: se abrió ${this.nombreArea}.`
          : "";
    }
    // Volver a leer los catálogos (Reintentar) no emite `cambio`: se consulta aquí.
    if (!primeraVez || this.anio != null) {
      this.consultar();
    }
  }

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    const previo = this.anio;
    if (previo == null || previo === seleccion.anio || !this.sucio) {
      this.anio = seleccion.anio;
      this.consultar();
      return;
    }
    this.pregunta(`Hay cambios sin guardar en ${previo}. Si cambias a ${seleccion.anio} se pierden.`, (si) => {
      if (si) {
        this.anio = seleccion.anio;
        this.consultar();
      } else {
        this.selector?.fijarAnio(previo);
      }
    });
  }

  cambiaArea(evento: Event): void {
    const combo = evento.target as HTMLSelectElement;
    const nueva = idValido(combo.value);
    const previa = this.idBscArea;
    if (nueva == null || nueva === previa) {
      return;
    }
    const aplica = () => {
      this.idBscArea = nueva;
      this.avisoArea = "";
      this.consultar();
    };
    if (!this.sucio) {
      aplica();
      return;
    }
    this.pregunta(`Hay cambios sin guardar en ${this.nombreArea}. Si cambias de área se pierden.`, (si) => {
      if (si) {
        aplica();
      } else if (this.comboArea) {
        this.comboArea.nativeElement.value = `${previa}`;
      }
    });
  }

  /** Reintentar cuando no se pudieron leer los catálogos. */
  recargarCatalogos(): void {
    this.selector?.recargar();
  }

  /* ============================ Consulta ============================ */

  consultar(): void {
    if (!this.catalogosCargados) {
      // El selector emite `cambio` aunque catalogos.do falle: sin áreas no se afirma nada.
      this.estado = this.anio == null ? "CARGANDO" : "SIN_CATALOGOS";
      this.cdr.detectChanges();
      return;
    }
    if (this.anio == null) {
      return;
    }
    if (this.idBscArea == null) {
      this.estado = "SIN_AREAS";
      this.limpia();
      this.cdr.detectChanges();
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { anio, idBscArea },
      replaceUrl: true,
    });
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.estado = "CARGANDO";
    this.cdr.detectChanges();

    this.consulta = this.bscService.planeacionConsultar(anio, idBscArea).subscribe({
      next: (res) => {
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.loading = false;
        if (!res || res.codigoError) {
          this.falla(this.mensaje(res, "No se pudo consultar la planeación."));
          return;
        }
        this.aplica(res);
        this.cdr.detectChanges();
      },
      error: () => {
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.loading = false;
        this.falla("No se pudo consultar la planeación: sin conexión con el servidor.");
      },
    });
  }

  private falla(texto: string): void {
    this.error = texto;
    this.estado = "ERROR";
    this.limpia();
    this.cdr.detectChanges();
  }

  private limpia(): void {
    this.ciclo = null;
    this.ejercicio = null;
    this.ejercicioOrigen = null;
    this.kpisOrigen = [];
    this.candidatosOrigen = [];
    this.filas = [];
    this.faltantes = [];
    this.avisos = [];
    this.movimientos = [];
    this.regreso = null;
    this.envio = null;
    this.autorizacion = null;
    this.puedeEditar = false;
    this.sucio = false;
    this.huella = "";
  }

  /** Pinta la respuesta (la de consultar y la de cada escritura: todas contestan el estado de T3). */
  private aplica(res: BscRes): void {
    this.ciclo = res.ciclo ?? null;
    this.ejercicio = res.ejercicio ?? null;
    this.ejercicioOrigen = res.ejercicioOrigen ?? null;
    this.kpisOrigen = res.kpisOrigen ?? [];
    this.puedeEditar = res.puedeEditar === true;
    this.puedePlanear = res.puedePlanear === true;
    this.puedeAutorizar = res.puedeAutorizar === true;
    this.tienePin = res.tienePin === true;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true || this.puedeConfigurar;
    this.estado = estadoDe(this.ejercicio, this.ciclo);

    // Las unidades: el mismo arreglo mientras no cambien (mat-select no recrea sus opciones).
    const unidades = res.unidades && res.unidades.length > 0 ? res.unidades : UNIDADES_POR_OMISION;
    const extra = (res.kpis ?? [])
      .map((k) => k.unidad)
      .filter((u): u is string => !!u && !unidades.includes(u));
    const todas = [...unidades, ...Array.from(new Set(extra))];
    if (todas.join("|") !== this.opcionesUnidad.map((o) => o.valor).join("|")) {
      this.opcionesUnidad = todas.map(opcionUnidad);
    }

    this.origenPorKpi = new Map();
    for (const o of this.kpisOrigen) {
      if (o.idKpi != null) {
        this.origenPorKpi.set(o.idKpi, o);
      }
    }
    const enPlaneacion = new Set((res.kpis ?? []).map((k) => k.idKpi));
    this.candidatosOrigen = this.kpisOrigen
      .filter((o) => o.idKpi != null && !enPlaneacion.has(o.idKpi))
      .map((o) => ({
        idKpi: o.idKpi as number,
        objetivo: (o.objetivo ?? "").trim() || `KPI ${o.idKpi}`,
        indicador: (o.indicador ?? "").trim(),
        meta: meta(o.metaValor, o.metaTexto, o.unidad),
      }));
    this.filas = (res.kpis ?? []).map((k) => this.filaDe(k));
    this.faltantes = (res.faltantes ?? []).map(parte);
    this.avisos = (res.avisos ?? []).map(parte);
    this.armaBitacora(res.historial ?? []);
    this.armaCiclo(res.ciclo ?? null, res.trimestresCiclo ?? []);
    this.queryCargaExcel = { modo: "PLANEACION", anio: this.anio ?? this.anioEnCurso };
    this.queryTablero = this.anio != null ? { anio: this.anio } : {};
    this.filas.forEach((f) => this.recalcula(f, false));
    this.huella = this.huellaActual();
    this.resumen();
  }

  private filaDe(k: BscEjercicioKpi): FilaPlan {
    const o = k.idKpi != null ? this.origenPorKpi.get(k.idKpi) ?? null : null;
    const metaInicial = textoMetaEditable(k);
    const objetivo = (k.objetivo ?? "").trim();
    const f: FilaPlan = {
      clave: `k${k.idEjercicioKpi}`,
      idEjercicioKpi: k.idEjercicioKpi ?? null,
      idKpi: k.idKpi ?? null,
      nueva: false,
      objetivo,
      indicador: (k.indicador ?? "").trim(),
      conservar: k.estatus !== "RETIRADO",
      metaEscrita: metaInicial,
      metaInicial,
      metaValorGuardado: k.metaValor ?? null,
      metaTextoGuardado: k.metaTexto ?? null,
      unidad: k.unidad || "%",
      pesos: [aPct(k.pesoQ1), aPct(k.pesoQ2), aPct(k.pesoQ3), aPct(k.pesoQ4)],
      na: [k.aplicaQ1 === false, k.aplicaQ2 === false, k.aplicaQ3 === false, k.aplicaQ4 === false],
      pesoAntesDeNa: [null, null, null, null],
      motivo: k.motivoCambioMeta ?? "",
      origen: o,
      metaAnterior: "",
      alcanceAnterior: "",
      pesosAnteriores: "",
      suma: "",
      sumaClase: "",
      sumaNota: "",
      metaCambiada: false,
      pesosCambiados: false,
      motivoPendiente: false,
      conMotivo: false,
      pesosConError: false,
      editada: false,
      huellaInicial: "",
      avisos: (k.avisos ?? []).map(parte),
      nombre: objetivo || "KPI sin objetivo",
    };
    this.ponOrigen(f, o, k);
    f.huellaInicial = huellaFila(f);
    return f;
  }

  /** Meta, alcance y pesos del año de origen como se leen en la tabla. */
  private ponOrigen(f: FilaPlan, o: BscEjercicioKpi | null, k?: BscEjercicioKpi): void {
    f.origen = o;
    if (!o) {
      f.metaAnterior = this.ejercicioOrigen ? "Nuevo" : SIN_DATO;
      f.alcanceAnterior = SIN_DATO;
      f.pesosAnteriores = "";
      return;
    }
    f.metaAnterior = meta(o.metaValor, o.metaTexto, o.unidad);
    const alcance = k?.alcanceAnterior;
    const hasta = k?.trimestreAlcanceAnterior;
    const alQ = hasta && hasta < 4 ? ` al Q${hasta}` : "";
    f.alcanceAnterior = alcance == null ? SIN_DATO : `${pct(alcance)}${alQ}`;
    f.pesosAnteriores = TRIMESTRES.map((q) =>
      aplicaDe(o, q) ? `${numeroCorto(aPct(pesoDe(o, q)) ?? 0)}` : "NA",
    ).join("/");
  }

  private armaBitacora(historial: BscHistorial[]): void {
    // Lo más reciente primero (el servidor la manda por fecha).
    this.movimientos = [...historial].reverse().map((h, i) => ({
      clave: `${h.id ?? i}`,
      tipo: h.accion ?? "",
      accion: ACCION_HISTORIAL[h.accion ?? ""] ?? h.accion ?? "",
      quien: (h.usuario ?? "").trim() || (h.idUsuario != null ? `Usuario ${h.idUsuario}` : SIN_DATO),
      cuando: fechaHora(h.fecha),
      detalle: detalleMovimiento(h),
    }));
    const ultimo = (accion: string) => this.movimientos.find((m) => m.tipo === accion) ?? null;
    this.regreso = ultimo("REGRESA_PLANEACION");
    this.envio = ultimo("ENVIO_PLANEACION");
    this.autorizacion = ultimo("AUTORIZA_PLANEACION");
  }

  private armaCiclo(ciclo: BscCiclo | null, trimestres: BscCicloTrimestre[]): void {
    if (!ciclo) {
      this.fechasCiclo = "";
      this.objetivoSolser = "";
      return;
    }
    this.objetivoSolser = pct(ciclo.objetivoSolser);
    const limites = TRIMESTRES.map((q) => {
      const t = trimestres.find((x) => x.trimestre === q);
      return `Q${q} ${t?.fechaLimiteEnvio ? fechaCorta(t.fechaLimiteEnvio) : "sin fecha"}`;
    });
    const dia = ciclo.diaLimiteCapturaMensual;
    const captura = dia != null ? `Captura mensual: día ${dia} del mes siguiente · ` : "";
    this.fechasCiclo = `${captura}Límite de envío: ${limites.join(", ")}`;
  }

  /* ============================ Estado de la planeación ============================ */

  get estatus(): EstatusPlaneacionBsc | null {
    return this.ejercicio?.estatus ?? null;
  }

  get estadoPlaneacion(): EstadoBsc<EstatusPlaneacionBsc> | null {
    const e = this.estatus;
    return e ? (ESTATUS_PLANEACION.find((x) => x.valor === e) ?? null) : null;
  }

  /** Se editan las metas: BORRADOR/REGRESADA, año abierto, hoja planeación y el área (lo dice el servidor). */
  get editable(): boolean {
    return this.estado === "PLANEACION" && this.puedeEditar;
  }

  get bloqueada(): boolean {
    return this.estatus === "ENVIADA" || this.estatus === "AUTORIZADA";
  }

  get cicloCerrado(): boolean {
    return this.ciclo?.estatus === "CERRADO";
  }

  /** Dirección resuelve una planeación enviada. */
  get puedeResolver(): boolean {
    return this.estado === "PLANEACION" && this.estatus === "ENVIADA" && this.puedeAutorizar && !this.cicloCerrado;
  }

  /** Abrirla (crear o cargar el FOR-011): año en curso o siguiente, año abierto y el área en mis manos. */
  get puedeAbrir(): boolean {
    return (
      this.estado === "SIN_PLANEACION" &&
      this.puedePlanear &&
      !this.cicloCerrado &&
      this.anio != null &&
      this.anio >= this.anioEnCurso
    );
  }

  /** El FOR-011 en modo PLANEACION: con la hoja y si el área admite Excel. */
  get puedeCargar(): boolean {
    const area = this.opcionesArea.find((a) => a.idBscArea === this.idBscArea);
    return this.puedeCargarExcel && area?.via !== "GUIADO" && (this.puedeAbrir || this.editable);
  }

  get nombreArea(): string {
    return (
      this.ejercicio?.area ||
      this.opcionesArea.find((a) => a.idBscArea === this.idBscArea)?.nombre ||
      "el área"
    );
  }

  get titulo(): string {
    return `Planeación BSC ${this.anio ?? ""} · ${this.nombreArea}`;
  }

  /** Debajo del título: de dónde salió la planeación y qué se puede hacer. */
  get resumenHero(): string {
    if (this.estado !== "PLANEACION") {
      return "Al inicio de cada año, cada área fija sus KPIs, metas y pesos y los envía a autorización.";
    }
    if (this.estatus === "AUTORIZADA") {
      return "Autorizada: metas, pesos y NA quedan fijos todo el año; los avances se capturan sobre ellos.";
    }
    if (this.estatus === "ENVIADA") {
      return "Enviada a autorización: Dirección la revisa. Mientras, no se edita.";
    }
    const origen = this.ejercicioOrigen?.anio;
    const base = origen ? `Creada a partir de ${origen}.` : "Sin año de origen.";
    return this.editable
      ? `${base} Ajusta metas y pesos, quita o agrega KPIs. Una vez autorizada, metas y pesos quedan bloqueados todo el año.`
      : base;
  }

  /** "Metas bloqueadas · autorizada el 20 ene 2027" o "Bloqueada · enviada a autorización el …". */
  get textoCandado(): string {
    if (this.estatus === "AUTORIZADA") {
      const importada = (this.ejercicio?.origen ?? "").startsWith("IMPORTACION");
      const fecha = fechaCorta(this.ejercicio?.fechaAutorizacion, "");
      return importada
        ? `Metas bloqueadas · importada del FOR-011${elDia(fecha)}`
        : `Metas bloqueadas · autorizada${elDia(fecha)}`;
    }
    if (this.estatus === "ENVIADA") {
      const fecha = fechaCorta(this.ejercicio?.fechaEnvio, "");
      return `Bloqueada · enviada a autorización${elDia(fecha)}`;
    }
    if (this.cicloCerrado) {
      return "Solo lectura · el año está cerrado";
    }
    return "Solo lectura · no puedes editar esta área";
  }

  get conservadas(): number {
    return this.filas.filter((f) => f.conservar).length;
  }

  /**
   * La casilla «Conservar todos» es de los KPI que ya están en la planeación:
   * una fila nueva no tiene casilla (se quita con ✕) y siempre se agrega
   * ACTIVA, así que no cuenta ni se desmarca.
   */
  get hayExistentes(): boolean {
    return this.filas.some((f) => !f.nueva);
  }

  get todasConservadas(): boolean {
    return this.hayExistentes && this.filas.every((f) => f.nueva || f.conservar);
  }

  get algunaConservada(): boolean {
    return this.filas.some((f) => !f.nueva && f.conservar);
  }

  get ocupado(): boolean {
    return this.loading || this.guardando || this.enviando || this.creando;
  }

  get puedeEnviar(): boolean {
    return this.editable && !this.ocupado && this.conservadas > 0 && (this.sucio || this.faltantes.length === 0);
  }

  /** Por qué no se puede enviar todavía, al lado del botón. */
  get razonNoEnvio(): string {
    if (!this.editable) {
      return "";
    }
    if (this.conservadas === 0) {
      return "La planeación necesita al menos un KPI.";
    }
    if (!this.sucio && this.faltantes.length > 0) {
      return "Resuelve lo que impide enviar.";
    }
    return "";
  }

  /* ============================ Edición ============================ */

  /** Después de cada edición de la fila: la previa de pesos y motivo, y si hay cambios sin guardar. */
  recalcula(f: FilaPlan, resume = true): void {
    const aplica = f.na.map((na) => !na);
    const fracciones = f.pesos.map((p) => aFraccion(p));
    const r = revisarPesos(
      aplica,
      fracciones,
      this.ciclo?.exigirSumaPesos ?? EXIGIR_SUMA_POR_OMISION,
      this.ciclo?.toleranciaPesos ?? TOLERANCIA_POR_OMISION,
    );
    f.suma = `${numeroCorto(Math.round(r.suma * 1000) / 10)}%`;
    f.pesosConError = f.conservar && r.codigo === "VAL-BSC-PESOS";
    ponSuma(f, r, aplica, fracciones);

    const editadaMeta = f.metaEscrita.trim() !== f.metaInicial.trim();
    const o = f.origen;
    if (o) {
      f.metaCambiada = metaDistinta(
        { valor: o.metaValor, texto: o.metaTexto, unidad: o.unidad },
        {
          valor: editadaMeta ? metaDeTexto(f.metaEscrita, f.unidad) : f.metaValorGuardado,
          texto: editadaMeta ? f.metaEscrita : f.metaTextoGuardado,
          unidad: f.unidad,
        },
      );
      f.pesosCambiados = pesosDistintos(
        TRIMESTRES.map((q) => aplicaDe(o, q)),
        TRIMESTRES.map((q) => pesoDe(o, q) ?? null),
        aplica,
        fracciones,
      );
    } else {
      f.metaCambiada = false;
      f.pesosCambiados = false;
    }
    f.motivoPendiente = f.conservar && f.metaCambiada && !f.motivo.trim();
    f.conMotivo = !!o && (f.conMotivo || f.metaCambiada || f.pesosCambiados || !!f.motivo.trim());
    f.editada = huellaFila(f) !== f.huellaInicial;
    if (resume) {
      this.resumen();
    }
  }

  /** Conteos del pie y si hay cambios sin guardar. */
  private resumen(): void {
    this.filasConPesosMal = this.filas.filter((f) => f.pesosConError).length;
    this.motivosPendientes = this.filas.filter((f) => f.motivoPendiente).length;
    this.sucio = this.huellaActual() !== this.huella;
  }

  /**
   * Al escribir la meta, el combo de unidad sigue a lo que el texto dice
   * («95%» → %, «12 días» → días, «6 productos» → No): en el servidor el combo
   * gana, y «3 defectos» con % se guardaría como 300 %. Un número solo no lo
   * mueve. El combo se puede cambiar después.
   */
  cambiaMeta(f: FilaPlan, texto: string): void {
    f.metaEscrita = texto ?? "";
    const unidad = unidadDeMeta(f.metaEscrita);
    if (unidad && unidad !== f.unidad && this.opcionesUnidad.some((u) => u.valor === unidad)) {
      f.unidad = unidad;
    }
    this.recalcula(f);
  }

  /** Marcar NA deja el peso en 0 (si no, cuenta en lo programado); desmarcar devuelve el que tenía. */
  alternaNa(f: FilaPlan, i: number, na: boolean): void {
    f.na[i] = na;
    if (na) {
      f.pesoAntesDeNa[i] = f.pesos[i];
      f.pesos[i] = 0;
    } else if (f.pesoAntesDeNa[i] != null) {
      f.pesos[i] = f.pesoAntesDeNa[i];
      f.pesoAntesDeNa[i] = null;
    }
    this.recalcula(f);
  }

  conservaFila(f: FilaPlan, conservar: boolean): void {
    f.conservar = conservar;
    this.recalcula(f);
  }

  /** Solo los KPI que ya están: una fila nueva se agrega activa (BscPlaneacionBuss.guardar) o se quita con ✕. */
  conservaTodas(conservar: boolean): void {
    this.filas
      .filter((f) => !f.nueva)
      .forEach((f) => {
        f.conservar = conservar;
        this.recalcula(f, false);
      });
    this.resumen();
  }

  agregarKpi(): void {
    if (!this.editable || this.ocupado) {
      return;
    }
    const clave = `n${++this.consecutivo}`;
    const f: FilaPlan = {
      clave,
      idEjercicioKpi: null,
      idKpi: null,
      nueva: true,
      objetivo: "",
      indicador: "",
      conservar: true,
      metaEscrita: "",
      metaInicial: "",
      metaValorGuardado: null,
      metaTextoGuardado: null,
      unidad: "%",
      pesos: [null, null, null, null],
      na: [false, false, false, false],
      pesoAntesDeNa: [null, null, null, null],
      motivo: "",
      origen: null,
      metaAnterior: "",
      alcanceAnterior: SIN_DATO,
      pesosAnteriores: "",
      suma: "",
      sumaClase: "",
      sumaNota: "",
      metaCambiada: false,
      pesosCambiados: false,
      motivoPendiente: false,
      conMotivo: false,
      pesosConError: false,
      editada: true,
      huellaInicial: "",
      avisos: [],
      nombre: "KPI nuevo",
    };
    this.ponOrigen(f, null);
    this.filas = [...this.filas, f];
    this.recalcula(f);
    this.cdr.detectChanges();
    const primero = this.candidatosOrigen.length > 0 ? `#bsc-plan-origen-${clave}` : `#bsc-plan-objetivo-${clave}`;
    this.enfoca([primero, `#bsc-plan-objetivo-${clave}`]);
  }

  /**
   * Una fila nueva puede ser un KPI del año de origen que no está en la
   * planeación (con su meta, unidad, pesos y NA) o uno nuevo (objetivo e
   * indicador escritos; si el maestro ya lo tiene, el servidor lo reutiliza).
   */
  eligeOrigen(f: FilaPlan, idKpi: number | null): void {
    const o = idKpi != null ? this.origenPorKpi.get(idKpi) ?? null : null;
    f.idKpi = o ? idKpi : null;
    if (o) {
      f.objetivo = (o.objetivo ?? "").trim();
      f.indicador = (o.indicador ?? "").trim();
      const texto = textoMetaEditable(o);
      f.metaEscrita = texto;
      f.metaInicial = texto;
      f.metaValorGuardado = o.metaValor ?? null;
      f.metaTextoGuardado = o.metaTexto ?? null;
      f.unidad = o.unidad || "%";
      f.pesos = TRIMESTRES.map((q) => aPct(pesoDe(o, q)));
      f.na = TRIMESTRES.map((q) => !aplicaDe(o, q));
    } else {
      f.objetivo = "";
      f.indicador = "";
      f.metaEscrita = "";
      f.metaInicial = "";
      f.metaValorGuardado = null;
      f.metaTextoGuardado = null;
    }
    f.pesoAntesDeNa = [null, null, null, null];
    f.nombre = f.objetivo || "KPI nuevo";
    this.ponOrigen(f, o);
    this.recalcula(f);
  }

  quitarNueva(f: FilaPlan): void {
    const i = this.filas.indexOf(f);
    this.filas = this.filas.filter((x) => x !== f);
    this.resumen();
    this.cdr.detectChanges();
    const siguiente = this.filas[Math.min(i, this.filas.length - 1)];
    this.enfoca(siguiente ? [`tr[data-clave="${siguiente.clave}"] input`] : ["#bsc-plan-agregar"]);
  }

  nombreDe(f: FilaPlan): void {
    f.nombre = f.objetivo.trim() || "KPI nuevo";
    this.recalcula(f);
  }

  /** Deshace lo que no se guardó: vuelve a pintar la última respuesta. */
  descartar(): void {
    if (!this.sucio || this.ocupado) {
      return;
    }
    this.pregunta("Se pierden los cambios que no guardaste.", (si) => {
      if (si) {
        this.consultar();
      }
    });
  }

  /* ============================ Crear ============================ */

  crear(desdeOrigen: boolean): void {
    if (!this.puedeAbrir || this.anio == null || this.idBscArea == null || this.ocupado) {
      return;
    }
    const anioOrigen = desdeOrigen ? (this.ejercicioOrigen?.anio ?? null) : null;
    if (desdeOrigen && anioOrigen == null) {
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    this.creando = true;
    this.cdr.detectChanges();
    this.peticion = this.bscService.planeacionCrear(anio, idBscArea, anioOrigen).subscribe({
      next: (res) => {
        this.creando = false;
        if (!res || res.codigoError) {
          this.cdr.detectChanges();
          this.msg.warningCall(this.mensaje(res, "No se pudo abrir la planeación."), "No se abrió la planeación", null);
          return;
        }
        if (anio === this.anio && idBscArea === this.idBscArea) {
          this.aplica(res);
          this.pintaYEnfoca(["#bsc-plan-titulo"]);
        }
      },
      error: () => {
        this.creando = false;
        this.cdr.detectChanges();
        this.msg.errorCall("No se pudo abrir la planeación: sin conexión con el servidor.", null, null);
      },
    });
  }

  /* ============================ Guardar y enviar ============================ */

  guardar(): void {
    this.guardarY(null);
  }

  /**
   * Manda las filas que cambiaron (las demás no se tocan) y pinta el estado
   * que contesta el servidor. `despues` sigue solo si se guardó (enviar).
   */
  private guardarY(despues: (() => void) | null): void {
    const idEjercicio = this.ejercicio?.idEjercicio;
    if (!this.editable || this.ocupado || idEjercicio == null) {
      return;
    }
    const cambiadas = this.filas.filter((f) => f.editada);
    if (cambiadas.length === 0) {
      if (despues) {
        despues();
      }
      return;
    }
    const errores = this.validaFilas(cambiadas);
    if (errores.length > 0) {
      this.msg.warningCall(errores.join(" "), "Revisa la planeación", null);
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    this.guardando = true;
    this.cdr.detectChanges();
    this.peticion = this.bscService.planeacionGuardar(idEjercicio, cambiadas.map((f) => this.aBean(f))).subscribe({
      next: (res) => {
        this.guardando = false;
        if (!res || res.codigoError) {
          this.pintaYEnfoca(["#bsc-plan-guardar"]);
          this.msg.warningCall(this.mensaje(res, "No se pudo guardar la planeación. No se guardó nada."), "No se guardó", null);
          return;
        }
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.aplica(res);
        this.cdr.detectChanges();
        if (despues) {
          despues();
          return;
        }
        this.enfoca(["#bsc-plan-guardar", "#bsc-plan-titulo"]);
        const cosas = this.faltantes.length === 1 ? "1 cosa" : `${this.faltantes.length} cosas`;
        const pendiente =
          this.faltantes.length > 0
            ? ` Para enviarla falta resolver ${cosas} (arriba de la tabla).`
            : "";
        this.msg.successCall(`El borrador de ${this.nombreArea} ${anio ?? ""} quedó guardado.${pendiente}`, "Guardado", null);
      },
      error: () => {
        this.guardando = false;
        this.pintaYEnfoca(["#bsc-plan-guardar"]);
        this.msg.errorCall("No se pudo guardar la planeación: sin conexión con el servidor.", null, null);
      },
    });
  }

  /** Lo que el servidor rechazaría sin escribir nada, para no hacer el viaje. */
  private validaFilas(filas: FilaPlan[]): string[] {
    const errores: string[] = [];
    for (const f of filas) {
      if (f.nueva && f.idKpi == null && !f.objetivo.trim()) {
        errores.push("Escribe el objetivo de cada KPI nuevo (o quítalo de la lista).");
      }
      if (f.pesos.some((p) => p != null && (Number.isNaN(Number(p)) || p < 0 || p > 100))) {
        errores.push(`«${f.nombre}»: cada peso va de 0 a 100 %.`);
      }
    }
    return Array.from(new Set(errores));
  }

  /** La fila como la lee BscPlaneacionBuss.guardar. */
  private aBean(f: FilaPlan): BscEjercicioKpi {
    const editadaMeta = f.metaEscrita.trim() !== f.metaInicial.trim();
    const b: BscEjercicioKpi = {
      unidad: f.unidad,
      pesoQ1: aFraccion(f.pesos[0]) ?? 0,
      pesoQ2: aFraccion(f.pesos[1]) ?? 0,
      pesoQ3: aFraccion(f.pesos[2]) ?? 0,
      pesoQ4: aFraccion(f.pesos[3]) ?? 0,
      aplicaQ1: !f.na[0],
      aplicaQ2: !f.na[1],
      aplicaQ3: !f.na[2],
      aplicaQ4: !f.na[3],
      // Vacío borra el motivo; el servidor lo pide al enviar si la meta cambió.
      motivoCambioMeta: f.motivo.trim(),
    };
    if (editadaMeta) {
      // El texto como se escribió: el servidor lo lee con BscMetaParser y la unidad del combo.
      b.metaTexto = f.metaEscrita.trim();
    } else {
      // Sin tocarla viajan el valor y el texto guardados: releer "33.33%" no cambia la meta.
      if (f.metaValorGuardado != null) {
        b.metaValor = f.metaValorGuardado;
      }
      if (f.metaTextoGuardado != null) {
        b.metaTexto = f.metaTextoGuardado;
      }
    }
    if (f.idEjercicioKpi != null) {
      b.idEjercicioKpi = f.idEjercicioKpi;
      b.estatus = f.conservar ? "ACTIVO" : "RETIRADO";
    } else if (f.idKpi != null) {
      b.idKpi = f.idKpi;
      b.objetivo = f.objetivo;
    } else {
      b.objetivo = f.objetivo.trim();
      if (f.indicador.trim()) {
        b.indicador = f.indicador.trim();
      }
    }
    return b;
  }

  enviar(): void {
    if (!this.puedeEnviar || this.ejercicio?.idEjercicio == null) {
      return;
    }
    const antes = this.sucio ? "Primero se guardan los cambios. " : "";
    this.pregunta(
      `${antes}La planeación ${this.anio ?? ""} de ${this.nombreArea} se envía a autorización: mientras Dirección la ` +
        "revisa no se edita, y al autorizarla metas y pesos quedan fijos todo el año.",
      (si) => {
        if (si) {
          this.guardarY(() => this.enviarYa());
        }
      },
    );
  }

  private enviarYa(): void {
    const idEjercicio = this.ejercicio?.idEjercicio;
    if (idEjercicio == null || this.ocupado) {
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    this.enviando = true;
    this.cdr.detectChanges();
    this.peticion = this.bscService.planeacionEnviar(idEjercicio).subscribe({
      next: (res) => {
        this.enviando = false;
        if (!res || res.codigoError) {
          this.pintaYEnfoca(["#bsc-plan-enviar", "#bsc-plan-titulo"]);
          this.msg.warningCall(this.mensaje(res, "No se pudo enviar la planeación."), "No se envió", null);
          // Lo que lo impidió (o quién se adelantó) sale en la consulta.
          if (res?.codigoError !== "AUTH-001") {
            this.consultar();
          }
          return;
        }
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.aplica(res);
        this.pintaYEnfoca(["#bsc-plan-titulo"]);
        this.msg.successCall(
          `La planeación ${anio ?? ""} de ${this.nombreArea} se envió a autorización. Dirección la revisa; mientras, no se edita.`,
          "Enviada",
          null,
        );
      },
      error: () => {
        this.enviando = false;
        this.pintaYEnfoca(["#bsc-plan-enviar"]);
        this.msg.errorCall("No se pudo enviar la planeación: sin conexión con el servidor.", null, null);
      },
    });
  }

  /* ============================ Autorizar o regresar (Dirección, con PIN) ============================ */

  resolver(aprobar: boolean): void {
    const idEjercicio = this.ejercicio?.idEjercicio;
    if (!this.puedeResolver || idEjercicio == null || this.ocupado) {
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    const area = this.nombreArea;
    this.dialogo = abrirDialogoPinBsc(this.dialog, {
      aprobar,
      eyebrow: aprobar ? "Autorizar planeación" : "Regresar planeación",
      titulo: `${area} · planeación ${anio ?? ""}`,
      detalle: aprobar
        ? `Metas, pesos y NA quedan fijos todo ${anio ?? "el año"}; se abren los cuatro trimestres y Q1 queda en captura.`
        : "Vuelve al área para corregirla y enviarla de nuevo; el comentario queda en la bitácora.",
      textoConfirmar: aprobar ? "Autorizar" : "Regresar",
      enviar: (pin, comentario) => this.bscService.planeacionResolver(idEjercicio, aprobar, comentario || null, pin),
    });
    this.dialogo.afterClosed().subscribe((res) => {
      this.dialogo = undefined;
      if (!res || this.destruido) {
        return;
      }
      if (res.codigoError) {
        // Se canceló después de un error que no es del PIN (otro ya la
        // resolvió, el año se cerró): lo que se ve ya no vale. Como enviarYa,
        // se vuelve a consultar, salvo con la sesión caducada.
        if (res.codigoError !== "AUTH-001" && anio === this.anio && idBscArea === this.idBscArea) {
          this.consultar();
          this.enfoca(["#bsc-plan-titulo"]);
        }
        return;
      }
      if (anio === this.anio && idBscArea === this.idBscArea) {
        this.aplica(res);
      }
      this.pintaYEnfoca(["#bsc-plan-titulo"]);
      this.msg.successCall(
        aprobar
          ? `La planeación ${anio ?? ""} de ${area} quedó autorizada: metas y pesos fijos y Q1 en captura.`
          : `La planeación ${anio ?? ""} de ${area} se regresó al área con tu comentario.`,
        aprobar ? "Autorizada" : "Regresada",
        null,
      );
    });
  }

  /* ============================ Presentación ============================ */

  /** La meta de un KPI del año de origen como se lee ("95%", "6 productos"). */
  textoMetaOrigen(o: BscEjercicioKpi): string {
    return meta(o.metaValor, o.metaTexto, o.unidad);
  }

  trackFila(_: number, f: FilaPlan): string {
    return f.clave;
  }

  trackMensaje(i: number, m: MensajeBsc): string {
    return `${i}:${m.codigo}`;
  }

  trackMovimiento(_: number, m: Movimiento): string {
    return m.clave;
  }

  /* ============================ Utilerías ============================ */

  private huellaActual(): string {
    return JSON.stringify(this.filas.map((f) => [f.clave, huellaFila(f)]));
  }

  /** questionCall con la guarda de la pantalla destruida. */
  private pregunta(texto: string, responde: (si: boolean) => void): void {
    this.preguntaAbierta = true;
    this.msg.questionCall(texto, null, (r: { isConfirmed?: boolean }) => {
      this.preguntaAbierta = false;
      if (!this.destruido) {
        responde(r?.isConfirmed === true);
      }
    });
  }

  private pintaYEnfoca(selectores: string[]): void {
    this.cdr.detectChanges();
    this.enfoca(selectores);
  }

  /**
   * El primero que exista y se pueda enfocar: lo que tenía el foco pudo
   * desaparecer (un botón que ya no aplica) o quedar deshabilitado («Guardar
   * borrador» sin cambios después de guardar); focus() en un control
   * deshabilitado no hace nada y el foco se perdería en el body.
   */
  private enfoca(selectores: string[]): void {
    setTimeout(() => {
      for (const s of selectores) {
        const el = this.host.nativeElement.querySelector<HTMLElement>(s);
        if (el && !el.matches(':disabled, [aria-disabled="true"]')) {
          el.focus();
          return;
        }
      }
    });
  }

  private mensaje(res: BscRes | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || porOmision;
  }
}

/* ============================ Funciones puras ============================ */

function opcionUnidad(valor: string): OpcionUnidad {
  return { valor, texto: valor === "dias" ? "días" : valor };
}

/** Qué enseña la pantalla con la respuesta: la planeación, abrirla o el año sin ciclo. */
function estadoDe(ejercicio: BscEjercicio | null, ciclo: BscCiclo | null): EstadoPlaneacion {
  if (ejercicio) {
    return "PLANEACION";
  }
  return ciclo ? "SIN_PLANEACION" : "SIN_CICLO";
}

/** " el 20 ene 2027" para seguir al candado; vacío sin fecha. */
function elDia(fecha: string): string {
  return fecha ? ` el ${fecha}` : "";
}

/**
 * El color y la nota de la suma de pesos de la fila (recalcula): se quita, no
 * aplica, pesos que no cuadran, inicio después de Q1, NA con peso o en orden.
 */
function ponSuma(f: FilaPlan, r: RevisionPesos, aplica: boolean[], fracciones: (number | null)[]): void {
  if (!f.conservar) {
    f.sumaClase = "";
    f.sumaNota = "Se quita";
  } else if (r.trimestreInicio == null) {
    f.sumaClase = "is-error";
    f.sumaNota = "No aplica en ningún trimestre";
  } else if (r.codigo === "VAL-BSC-PESOS") {
    f.sumaClase = "is-error";
    f.sumaNota = fracciones.some((p, i) => aplica[i] && (p ?? 0) < 0) ? "Hay un peso negativo" : "Deben sumar 100%";
  } else if (r.codigo === "PESOS_PARCIALES") {
    f.sumaClase = "is-aviso";
    f.sumaNota = `Inicia en Q${r.trimestreInicio}: solo se avisa`;
  } else if (r.pesoEnNa.length > 0) {
    const conPeso = r.pesoEnNa.map((q) => `Q${q}`).join(", ");
    f.sumaClase = "is-aviso";
    f.sumaNota = `${conPeso} NA con peso`;
  } else {
    f.sumaClase = "is-ok";
    f.sumaNota = "";
  }
}

function anioValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 1900 && n < 3000 ? n : null;
}

function idValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 0 ? n : null;
}

/** Fracción → porcentaje sin el ruido del punto flotante (0.25 → 25; 0.333333 → 33.3333). */
function aPct(fraccion: number | null | undefined): number | null {
  if (fraccion == null) {
    return null;
  }
  return Math.round(Number(fraccion) * 100 * 10000) / 10000;
}

/** Porcentaje → fracción con 6 decimales (25 → 0.25), como viaja en la API; vacío = nulo. */
function aFraccion(porcentaje: number | null | undefined): number | null {
  if (porcentaje == null || (porcentaje as unknown) === "" || Number.isNaN(Number(porcentaje))) {
    return null;
  }
  return Number((Number(porcentaje) / 100).toFixed(6));
}

/** Sin separador de miles (el lector de metas no adivina "1,000") y sin ceros de más. */
function numeroCorto(n: number, decimales = 4): string {
  return `${Number(Number(n).toFixed(decimales))}`;
}

/** La meta en el campo: el texto con que se escribió o el número ("95%" en %, "6" en No o días). */
function textoMetaEditable(k: BscEjercicioKpi): string {
  const texto = (k.metaTexto ?? "").trim();
  if (texto) {
    return texto;
  }
  if (k.metaValor == null) {
    return "";
  }
  return !k.unidad || k.unidad === "%"
    ? `${numeroCorto(Number(k.metaValor) * 100, 2)}%`
    : numeroCorto(Number(k.metaValor));
}

function aplicaDe(k: BscEjercicioKpi, q: number): boolean {
  return [k.aplicaQ1, k.aplicaQ2, k.aplicaQ3, k.aplicaQ4][q - 1] !== false;
}

function pesoDe(k: BscEjercicioKpi, q: number): number | undefined {
  return [k.pesoQ1, k.pesoQ2, k.pesoQ3, k.pesoQ4][q - 1];
}

/** Lo editable de una fila, para saber si cambió. */
function huellaFila(f: FilaPlan): string {
  return JSON.stringify([
    f.idKpi,
    f.objetivo.trim(),
    f.indicador.trim(),
    f.conservar,
    f.metaEscrita.trim(),
    f.unidad,
    f.pesos.map((p) => (p == null || (p as unknown) === "" ? null : Number(p))),
    f.na,
    f.motivo.trim(),
  ]);
}

/** "CODIGO Q2: mensaje" → { codigo, "Q2 · mensaje" }; un texto sin código se queda tal cual. */
function parte(texto: string): MensajeBsc {
  const m = FORMATO_MENSAJE.exec(texto ?? "");
  if (!m) {
    return { codigo: "", texto: texto ?? "" };
  }
  return { codigo: m[1], texto: m[2] ? `${m[2]} · ${m[3]}` : m[3] };
}

/** Lo que dice un renglón de la bitácora: el comentario, o el cambio (anterior → nuevo). */
function detalleMovimiento(h: BscHistorial): string {
  const partes: string[] = [];
  if (h.valorAnterior || h.valorNuevo) {
    partes.push(
      h.valorAnterior && h.valorNuevo ? `${h.valorAnterior} → ${h.valorNuevo}` : (h.valorNuevo ?? h.valorAnterior ?? ""),
    );
  }
  if (h.comentario) {
    partes.push(`«${h.comentario}»`);
  }
  return partes.join(" · ");
}
